import {find, isEmpty, isNil} from 'lodash';
import {CSSProperties} from 'react';

const EVENT_ID = 'encounter_id';
const EVENT_OCCURRENCE_DATE = 'occurance_date';
const EVENT_DAYS = 'days';
const EVENT_NI_TYPE = 'ni_type';
const EVENT_ANNOTATIONS = 'annot';

const EVENT_ANNOTATION_LABEL = 'label';
const EVENT_ANNOTATION_DOCUMENT_ID = 'documentid';
const EVENT_ANNOTATION_DOCUMENT_TYPE = 'documenttype';
const EVENT_ANNOTATION_STYLE = 'style';

const RESULT_RESPONSE_DATA = 'data';

const STATIC_EMPTY_OBJECT = {};

export interface Annotations {
  label: string;
  documentid: string | null;
  documenttype: string | null;
  style: CSSProperties | null;
}

export interface Events {
  encounter_id: string;
  occurance_date: string;
  ni_type: string;
  days: number;
  maxSubRows: number;
  annot: Annotations[];
}

export interface EncounterTimelineRaw {
  data: Events[];
}

export class EncounterTimelineEventAnnotation {
  private readonly rawData: Partial<Annotations>;

  static isNotEmptyStyle(
    style: CSSProperties | Record<string, never>
  ): style is CSSProperties {
    return !isEmpty(style);
  }

  constructor(rawData = {}) {
    this.rawData = {...rawData};
  }

  getRawData() {
    return this.rawData;
  }

  getLabel() {
    return this.rawData[EVENT_ANNOTATION_LABEL];
  }

  getDocumentId() {
    return this.rawData[EVENT_ANNOTATION_DOCUMENT_ID];
  }

  getDocumentType() {
    return this.rawData[EVENT_ANNOTATION_DOCUMENT_TYPE];
  }

  getStyle(): CSSProperties | Record<string, never> {
    return this.rawData[EVENT_ANNOTATION_STYLE] || STATIC_EMPTY_OBJECT;
  }
}

export interface TimelineAnnotationTypeFormatterValueProps {
  caseId: string | number;
  annotations: EncounterTimelineEventAnnotation[];
}

export class EncounterTimelineEvent {
  private readonly rawData: Partial<Events>;

  private readonly annotations: EncounterTimelineEventAnnotation[];

  static initAnnotations(rawDataAnnotations) {
    const annotations: EncounterTimelineEventAnnotation[] = [];
    if (rawDataAnnotations) {
      rawDataAnnotations.forEach((annotation) => {
        annotations.push(new EncounterTimelineEventAnnotation(annotation));
      });
    }
    return annotations;
  }

  constructor(rawData = {}) {
    this.rawData = {...rawData};
    this.annotations = [];
    if (this.rawData[EVENT_ANNOTATIONS]) {
      this.annotations = EncounterTimelineEvent.initAnnotations(
        this.rawData[EVENT_ANNOTATIONS]
      );
    }
  }

  getId() {
    return this.rawData[EVENT_ID];
  }

  getRawData() {
    return this.rawData;
  }

  getOccurenceDate() {
    return this.rawData[EVENT_OCCURRENCE_DATE];
  }

  getDays() {
    return this.rawData[EVENT_DAYS];
  }

  getNiType() {
    return this.rawData[EVENT_NI_TYPE];
  }

  getAnnotations() {
    return this.annotations;
  }

  getTableProjection(encounterId: string, metadata: any = {}) {
    return {
      ...this.getRawData(),
      ...this.getAnnotationsDataProjection(encounterId),
      ...metadata,
    };
  }

  getAnnotationsDataProjection(encounterId: string) {
    return {
      uiAnnotations: {
        annotations: this.getAnnotations(),
        encounterId,
      },
    };
  }
}

export class EncounterTimeline {
  static initEvents(rawDataEvents) {
    const events: EncounterTimelineEvent[] = [];
    if (rawDataEvents) {
      rawDataEvents.forEach((event) => {
        events.push(new EncounterTimelineEvent(event));
      });
    }
    return events;
  }

  private readonly rawData: Partial<EncounterTimelineRaw>;

  private readonly events: EncounterTimelineEvent[];

  constructor(rawData: Partial<EncounterTimelineRaw> = {}) {
    this.rawData = {...rawData};
    this.events = [];
    if (this.rawData[RESULT_RESPONSE_DATA]) {
      this.events = EncounterTimeline.initEvents(
        this.rawData[RESULT_RESPONSE_DATA]
      );
    }
  }

  getEvents() {
    return this.events;
  }

  getEvent(eventId) {
    return find(this.getEvents(), (event) => event.getId() === eventId);
  }
}

export default {
  EncounterTimeline,
  EncounterTimelineEvent,
  EncounterTimelineEventAnnotation,
};
